import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Theme,
  Typography,
  useTheme,
} from "@mui/material";
import ArrowForwardIos from "@mui/icons-material/ArrowForwardIos";
import DoubleArrowIcon from "@mui/icons-material/DoubleArrow";
import React, { useCallback, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { makeStyles } from "helpers/StyleHelper";

export class DualListItem {
  id: string;
  text: string;

  constructor(id?: string, text?: string) {
    this.id = id ? id : "";
    this.text = text ? text : "";
  }
}

export type DualListSelectorProps = {
  options: DualListItem[];
  selectedOptions: DualListItem[];
  isFetching?: boolean;
  readonly?: boolean;
  index: number;
  onChange: (selected: DualListItem[]) => void;
};

const useStyles = makeStyles((theme: Theme) =>
  ({
    paper: {
      width: "100%",
      height: "30vh",
      overflow: "auto",
    },
    button: {
      margin: theme.spacing(0.5, 2),
    },
    iconFlipped: {
      transform: "scaleX(-1)",
    },
  })
);

const not = (a: DualListItem[], b: DualListItem[]) => {
  return a.filter((itemA) => !b.find((itemB) => itemB.id === itemA.id));
};

const intersection = (listA: string[], listB: DualListItem[]) => {
  return listB.filter((b) => listA.indexOf(b.id) !== -1);
};

const DualListSelector = ({
  options,
  selectedOptions,
  onChange,
  isFetching,
  readonly,
}: DualListSelectorProps) => {
  const [checked, setChecked] = useState<string[]>([]);
  const styles = useStyles(useTheme());
  const { formatMessage: t } = useIntl();
  const [left, setLeft] = useState<DualListItem[]>([]);
  const [right, setRight] = useState<DualListItem[]>([]);

  const setColumns = useCallback(
    (items: DualListItem[], selectedItems: DualListItem[]) => {
      setRight(selectedItems);
      setLeft(not(items, selectedItems));
    },
    []
  );

  useEffect(() => {
    setColumns(options ?? [], selectedOptions ?? []);
  }, [options, selectedOptions, setColumns]);

  const leftChecked = intersection(checked, left);
  const rightChecked = intersection(checked, right);

  const handleToggle = (value: string) => () => {
    const currentIndex = checked.indexOf(value);
    const newChecked = [...checked];

    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    setChecked(newChecked);
  };

  const customList = (
    title: string,
    items: DualListItem[],
    fetching = false
  ) => (
    <Box display="flex" flexDirection="column" width={1}>
      <Typography variant="subtitle1" gutterBottom>
        {title}
      </Typography>
      <Paper sx={styles.paper}>
        {fetching && (
          <Box display="flex" my={2} justifyContent="center">
            <CircularProgress size={24} />
          </Box>
        )}
        <List dense component="div" role="list" aria-label={title}>
          {items.map((item: DualListItem) => {
            return (
              <ListItem
                key={item.id}
                role="listitem"
                button
                onClick={handleToggle(item.id)}
                disabled={readonly}
              >
                <ListItemIcon>
                  <Checkbox
                    checked={checked.indexOf(item.id) !== -1}
                    tabIndex={-1}
                    disableRipple
                  />
                </ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItem>
            );
          })}
          <ListItem />
        </List>
      </Paper>
    </Box>
  );

  const handleAllRight = () => {
    onChange(right.concat(left));
  };

  const handleCheckedRight = () => {
    setChecked(
      checked.filter((id) => !leftChecked.find((option) => option.id === id))
    );
    onChange(right.concat(leftChecked));
  };

  const handleCheckedLeft = () => {
    setChecked(
      checked.filter((id) => !rightChecked.find((item) => item.id === id))
    );
    onChange(not(right, rightChecked));
  };

  const handleAllLeft = () => {
    onChange([]);
  };

  return (
    <Box mt={3} display="flex" alignItems="center">
      {customList(t({ id: "availableLabel" }), left, isFetching)}
      <Box display="flex" flexDirection="column" alignItems="center">
        <Button
          variant="outlined"
          size="small"
          sx={styles.button}
          onClick={handleAllRight}
          disabled={readonly || left.length === 0}
          aria-label={t({ id: "moveAllToSelected" })}
        >
          <DoubleArrowIcon />
        </Button>
        <Button
          variant="outlined"
          size="small"
          sx={styles.button}
          onClick={handleCheckedRight}
          disabled={readonly || leftChecked.length === 0}
          aria-label={t({ id: "moveCheckedToSelected" })}
        >
          <ArrowForwardIos />
        </Button>
        <Button
          variant="outlined"
          size="small"
          sx={styles.button}
          onClick={handleCheckedLeft}
          disabled={readonly || rightChecked.length === 0}
          aria-label={t({ id: "moveCheckedToAvailable" })}
        >
          <ArrowForwardIos sx={styles.iconFlipped} />
        </Button>
        <Button
          variant="outlined"
          size="small"
          sx={styles.button}
          onClick={handleAllLeft}
          disabled={readonly || right.length === 0}
          aria-label={t({ id: "moveAllToAvailable" })}
        >
          <DoubleArrowIcon sx={styles.iconFlipped} />
        </Button>
      </Box>
      {customList(t({ id: "selectedLabel" }), right)}
    </Box>
  );
};

export default DualListSelector;
